import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import CustomPaper from '../common/CustomPaper';
import '../WebhookImport/WebhookImport.css';
import { send_component_save_request, send_request } from '../../utils/Request';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useTriggerFromZapierStore, { getTriggerFromZapierStore } from './useTriggerFromZapierStore';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import ChooseApp from './component/ChooseApp';
import SetupAction from './component/SetupAction';
import SkipTest from './component/SkipTest';
import SendSampleFromZapier from './component/SendSampleFromZapier';
import SampleReceived from './component/SampleReceived';
import SetUpPlaceholders from './component/SetUpPlaceholders';
import SetEndpointUrl from './component/SetEndpointURL';
import SetToken from './component/SetToken';
import StepButton from '@mui/material/StepButton';
import Summary from './component/Summary';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';

const TriggerFromZapierTemplate = (props) => {
  const [placeholders, setPlaceholders] = useState([]);
  const [checkKey, setCheckKey] = useState(false);
  const [data, setData] = useState({});
  const [token, setToken] = useState('');
  const [index, setIndex] = useState(0);
  const [checkRoutingKey, setCheckRoutingKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [placeholdersJson, setPlaceholdersJson] = useState([]);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [setShowPasswordState, setSetShowPassword] = useState(false);
  const [loadingCircle, setLoadingCircle] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [deleteLoadingCircle, setDeleteLoadingCircle] = useState(false);
  const [checkKeyUpload, setCheckKeyUpload] = useState(false);
  const [indexError, setIndexError] = useState([]);
  const [indexError2, setIndexError2] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [webhookStep, setWebhookStep] = useState(0);
  const [value, setValue] = useState(null);
  const [method, setMethod] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getTriggerFromZapierStore(props.component_id), [props.component_id]);

  const componentName = useProjectStore((s) => s.componentName);

  useEffect(() => {
    store.getState().setName(componentName);
  }, [componentName]);

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);
    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then((response) => {
        if (response && response.data) {
          const { components } = response.data;
          if (components) {
            store.getState().setComponentData(components[0]);
            store.setState({ loaded: true });
            props.onComponentDescriptionChanged(components[0].description);
            props.onComponentNameChanged(store.getState().name);
            props.onLastModifiedChanged(store.getState().lastModified);
            setStatus(components[0].status);

            if (components[0].componentData && components[0].componentData.data) {
              if (!components[0].componentData.data.samplePlaceholders) {
                const storeState = store.getState();
                const dataClone = { ...storeState.data, samplePlaceholders: [] };
                for (let i = 0; i < components[0].componentData.data.placeholders.length; i++) {
                  components[0].componentData.data.placeholders[i].checked = true;
                  dataClone.samplePlaceholders.push(
                    components[0].componentData.data.placeholders[i]
                  );
                }
                store.setState({ data: dataClone });
              }
              if (components[0].componentData.token) {
                setHasToken(true);
              }
              setPlaceholders(components[0].componentData.data.samplePlaceholders);
              setMethod(components[0].componentData.data.type);

              if (
                components[0].componentData.token &&
                components[0].componentData.data.json != '' &&
                components[0].componentData.data.samplePlaceholders != []
              ) {
                setActiveStep(7);
                setWebhookStep(7);
              }
              setLoading(false);
            } else {
              let lp = [];
              lp.push({
                index: 0,
                placeholders: [],
                key: store.getState().keyAuto(),
                incomingKey: '',
              });
              setPlaceholders(lp);
              setLoading(false);
            }
          }
        }
        props.setChildTemplateLoaded(true);
      })
      .catch((err) => {
        throw Error(err.message);
      });
  }, []);

  const handleSave = useCallback(() => {
    const storeState = store.getState();
    const { placeholderError } = storeState;
    if (error || placeholderError.length > 0) {
      props.showError();
      return;
    }

    let lastModified = getCurrentTime();
    let dataComponent = JSON.parse(JSON.stringify(storeState.data));
    const dataClone = { ...storeState.data, placeholders: [] };
    for (let i = 0; i < dataComponent.samplePlaceholders.length; i++) {
      if (dataComponent.samplePlaceholders[i].checked == true) {
        dataClone.placeholders.push(dataComponent.samplePlaceholders[i]);
      }
    }
    store.setState({ data: dataClone });

    let dataSave = JSON.parse(JSON.stringify(store.getState().data));
    const currentName = store.getState().name;
    const currentDescription = store.getState().componentDescription;

    let saveData = injectMetadataFields({
      componentData: {
        data: dataSave,
        name: currentName,
        lastModified: lastModified,
      },
      componentId: props.component_id,
      type: storeState.type,
      description: currentDescription,
    });

    send_component_save_request(
      'component-service/trigger_from_zapier/data',
      saveData,
      '',
      'POST'
    )
      .then((response) => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(saveData);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch((err) => {
        throw err;
      });
  }, [error, props.component_id]);

  const handleRoutingKeyChange = (event, placeholder, index) => {
    store.getState().onChangeField(event.target.value, placeholder, 'content', index);
  };

  const handleJsonBodyChange = (value) => {
    store.getState().onChangeField(value, '', 'json');
  };

  const handleKeyChanges = (err) => {
    setError(err);
  };

  const onChangeState = (newWebhookStep, newActiveStep) => {
    setWebhookStep(newWebhookStep);
    setActiveStep(newActiveStep);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ChooseApp
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            method={(m) => setMethod(m)}
            isExpanded={props.isExpanded}
          />
        );
      case 1:
        return (
          <SetupAction
            componentStore={store.getState()}
            status={status}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            changeTokenStatus={(s) => setHasToken(s)}
            hasToken={hasToken}
            project_id={props.project_id}
            component_id={props.component_id}
            method={method}
            isExpanded={props.isExpanded}
          />
        );
      case 2:
        return (
          <SendSampleFromZapier
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            project_id={props.project_id}
            component_id={props.component_id}
            version={props.version}
            isExpanded={props.isExpanded}
          />
        );
      case 3:
        return (
          <SampleReceived
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            handleKeyChanges={(err) => setError(err)}
            method={method}
            component_id={props.component_id}
            isExpanded={props.isExpanded}
          />
        );
      case 4:
        return (
          <SetUpPlaceholders
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            handleKeyChanges={(err) => setError(err)}
            method={method}
            stepStatus={'setPlaceholders'}
            isExpanded={props.isExpanded}
          />
        );
      case 5:
        return (
          <SetEndpointUrl
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            component_id={props.component_id}
            isExpanded={props.isExpanded}
          />
        );
      case 6:
        return (
          <SkipTest
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            component_id={props.component_id}
            isExpanded={props.isExpanded}
          />
        );
      case 7:
        return (
          <Summary
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            changeTokenStatus={(s) => setHasToken(s)}
            name={useProjectStore.getState().componentName}
            component_id={props.component_id}
            stepStatus={'summary'}
            hasToken={hasToken}
            isExpanded={props.isExpanded}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  const handleOnClickStep = (step) => {
    setWebhookStep(step);
    setActiveStep(step);
  };

  const steps = [
    'Choose app',
    'Set up action',
    'Test action in Zapier',
    'Sample received',
    'Set up placeholders',
    'Set endpoint URL',
    'Skip test',
    'Summary',
  ];

  if (!loading) {
    return (
      <Grid container xs={12} className="componentGrid">
        <CustomPaper className="componentPaperWebhook">
          <div
            style={{
              overflowX: 'auto',
              marginBottom: '24px',
              width: '100%',
            }}
          >
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              sx={{
                minWidth: 'min-content',
                padding: '8px',
                '& .MuiStep-root': {
                  minWidth: '160px',
                },
                '& .MuiStepIcon-root.Mui-active': {
                  color: '#55A77A',
                },
                '& .MuiStepIcon-root.Mui-completed': {
                  color: '#55A77A',
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepButton
                    onClick={() => {
                      handleOnClickStep(index);
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </div>

          <Grid
            container
            style={{ marginTop: props.isExpanded ? 40 : 0, padding: '0 24px' }}
          >
            {getStepContent(webhookStep)}
          </Grid>
        </CustomPaper>
      </Grid>
    );
  } else return <IndividualComponentLoader />;
};

export default TriggerFromZapierTemplate;
